import React, { useState, useEffect } from 'react';

import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PrimaryBtn } from 'components/UI/Buttons';
import { useEditBusinessUnitMessagesToNotifyMutation } from 'services/api/businessUnits.api';
import type { BusinessUnit, MessageToNotify } from 'services/types/businessUnit';

interface BusinessUnitAlertsEditorProps {
  businessUnit: BusinessUnit;
}

const BusinessUnitAlertsEditor = ({ businessUnit }: BusinessUnitAlertsEditorProps) => {
  const [messagesToNotifyDraft, setMessagesToNotifyDraft] = useState<MessageToNotify[]>(
    businessUnit.messagesToNotify || []
  );
  const [newEmails, setNewEmails] = useState<{ [alertIndex: number]: string }>({});
  const [newBranchOffices, setNewBranchOffices] = useState<{ [alertIndex: number]: string }>({});
  const [newBranchOfficeEmails, setNewBranchOfficeEmails] = useState<{
    [alertIndex: number]: { [branchOffice: string]: string };
  }>({});

  const [editBusinessUnitMessagesToNotify] = useEditBusinessUnitMessagesToNotifyMutation();

  useEffect(() => {
    setMessagesToNotifyDraft(businessUnit.messagesToNotify || []);
  }, [businessUnit]);

  const getEmailNotificationType = (alert: MessageToNotify): 'general' | 'branchOffice' | 'none' => {
    if (alert.emailsToNotify !== undefined && alert.emailsToNotify !== null) {
      return 'general';
    }
    if (alert.emailsToNotifyByBranchOffice !== undefined && alert.emailsToNotifyByBranchOffice !== null) {
      return 'branchOffice';
    }
    return 'none';
  };

  const handleEmailTypeChange = (alertIndex: number, type: 'general' | 'branchOffice' | 'none') => {
    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    // Clear both fields first
    delete alert.emailsToNotify;
    delete alert.emailsToNotifyByBranchOffice;

    // Set the appropriate field based on type
    if (type === 'general') {
      alert.emailsToNotify = [];
    } else if (type === 'branchOffice') {
      alert.emailsToNotifyByBranchOffice = {};
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAddGeneralEmail = (alertIndex: number, email: string) => {
    if (!email.trim()) {
      return;
    }

    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (!alert.emailsToNotify) {
      alert.emailsToNotify = [];
    }

    if (!alert.emailsToNotify.includes(email.trim())) {
      // Create a new array instead of mutating the existing one
      alert.emailsToNotify = [...alert.emailsToNotify, email.trim()];
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);

    // Clear the input
    setNewEmails((prev) => ({ ...prev, [alertIndex]: '' }));
  };

  const handleRemoveGeneralEmail = (alertIndex: number, email: string) => {
    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (alert.emailsToNotify) {
      alert.emailsToNotify = alert.emailsToNotify.filter((e) => e !== email);
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAddBranchOffice = (alertIndex: number, branchOffice: string) => {
    if (!branchOffice.trim()) return;

    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (!alert.emailsToNotifyByBranchOffice) {
      alert.emailsToNotifyByBranchOffice = {};
    }

    if (!alert.emailsToNotifyByBranchOffice[branchOffice.trim()]) {
      alert.emailsToNotifyByBranchOffice[branchOffice.trim()] = [];
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);

    // Clear the input
    setNewBranchOffices((prev) => ({ ...prev, [alertIndex]: '' }));
  };

  const handleRemoveBranchOffice = (alertIndex: number, branchOffice: string) => {
    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (alert.emailsToNotifyByBranchOffice) {
      delete alert.emailsToNotifyByBranchOffice[branchOffice];
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAddBranchOfficeEmail = (alertIndex: number, branchOffice: string, email: string) => {
    if (!email.trim()) return;

    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (!alert.emailsToNotifyByBranchOffice) {
      alert.emailsToNotifyByBranchOffice = {};
    } else {
      // Deep copy the emailsToNotifyByBranchOffice object
      alert.emailsToNotifyByBranchOffice = { ...alert.emailsToNotifyByBranchOffice };
    }

    if (!alert.emailsToNotifyByBranchOffice[branchOffice]) {
      alert.emailsToNotifyByBranchOffice[branchOffice] = [];
    }

    if (!alert.emailsToNotifyByBranchOffice[branchOffice].includes(email.trim())) {
      // Create a new array instead of mutating the existing one
      alert.emailsToNotifyByBranchOffice[branchOffice] = [
        ...alert.emailsToNotifyByBranchOffice[branchOffice],
        email.trim(),
      ];
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);

    // Clear the input
    setNewBranchOfficeEmails((prev) => ({
      ...prev,
      [alertIndex]: { ...prev[alertIndex], [branchOffice]: '' },
    }));
  };

  const handleRemoveBranchOfficeEmail = (alertIndex: number, branchOffice: string, email: string) => {
    const updatedAlerts = [...messagesToNotifyDraft];
    const alert = { ...updatedAlerts[alertIndex] };

    if (alert.emailsToNotifyByBranchOffice && alert.emailsToNotifyByBranchOffice[branchOffice]) {
      alert.emailsToNotifyByBranchOffice[branchOffice] = alert.emailsToNotifyByBranchOffice[branchOffice].filter(
        (e) => e !== email
      );
    }

    updatedAlerts[alertIndex] = alert;
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAddAlert = () => {
    setMessagesToNotifyDraft([
      ...messagesToNotifyDraft,
      {
        description: '',
        examples: [],
        slackChannelId: '',
        isLastCRMUpdatedThreeDaysAgo: false,
      },
    ]);
  };

  const handleDeleteAlert = (index: number) => {
    const updatedAlerts = [...messagesToNotifyDraft];
    updatedAlerts.splice(index, 1);
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAlertChange = (index: number, key: keyof MessageToNotify, value: string | boolean) => {
    const updatedAlerts = [...messagesToNotifyDraft];
    updatedAlerts[index] = { ...updatedAlerts[index], [key]: value };
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleAddExample = (alertIndex: number) => {
    const updatedAlerts = messagesToNotifyDraft.map((alert, idx) => {
      if (idx === alertIndex) {
        return {
          ...alert,
          examples: [...alert.examples, ''],
        };
      }
      return alert;
    });
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleExampleChange = (alertIndex: number, exampleIndex: number, value: string) => {
    const tempExamples = [...messagesToNotifyDraft[alertIndex].examples];
    tempExamples[exampleIndex] = value;
    const updatedAlerts = messagesToNotifyDraft.map((alert, idx) => {
      if (idx === alertIndex) {
        return {
          ...alert,
          examples: tempExamples,
        };
      }
      return alert;
    });
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleDeleteExample = (alertIndex: number, exampleIndex: number) => {
    const updatedAlerts = messagesToNotifyDraft.map((alert, idx) => {
      if (idx === alertIndex) {
        return {
          ...alert,
          examples: alert.examples.filter((_, exIdx) => exIdx !== exampleIndex),
        };
      }
      return alert;
    });
    setMessagesToNotifyDraft(updatedAlerts);
  };

  const handleSaveAlertsConfig = () => {
    const filteredAlertsConfig = messagesToNotifyDraft
      .filter((alert) => alert.description && alert.examples.length > 0)
      .map((alert) => ({
        ...alert,
        slackChannelId: alert.slackChannelId || undefined,
        emailsToNotify: alert.emailsToNotify?.length ? alert.emailsToNotify : undefined,
        emailsToNotifyByBranchOffice:
          alert.emailsToNotifyByBranchOffice && Object.keys(alert.emailsToNotifyByBranchOffice).length
            ? alert.emailsToNotifyByBranchOffice
            : undefined,
        isLastCRMUpdatedThreeDaysAgo: alert.isLastCRMUpdatedThreeDaysAgo ?? false,
      }));
    if (businessUnit) {
      editBusinessUnitMessagesToNotify({
        id: businessUnit.id,
        messagesToNotify: filteredAlertsConfig,
      })
        .then(() => {
          // eslint-disable-next-line no-alert
          window.alert('Alertas actualizadas correctamente');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error saving alerts config', error);
        });
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} my={3}>
      <Typography variant="h6">Alertas</Typography>
      RECUERDA GUARDAR las alertas al terminar de editar y probar.
      {messagesToNotifyDraft.map((alert, alertIndex) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={`alert-${alertIndex}`}
          sx={{
            border: '2px solid #a1a1a1',
            borderRadius: '8px',
            padding: '25px',
            mb: 2,
            backgroundColor: '#f6f6f6',
          }}
          display="flex"
          flexDirection="column"
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <Typography variant="h6">Alerta {alertIndex + 1}</Typography>
              </Grid>
              <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                <PrimaryBtn
                  startIcon={<Delete />}
                  onClick={() => handleDeleteAlert(alertIndex)}
                  variant="contained"
                  color="error"
                  sx={{ maxWidth: 200 }}
                >
                  Eliminar Alerta
                </PrimaryBtn>
              </Grid>
            </Grid>
            <TextField
              label="Descripción"
              value={alert.description}
              onChange={(e) => handleAlertChange(alertIndex, 'description', e.target.value)}
              required
              fullWidth
              multiline
              rows={2}
              InputProps={{
                inputComponent: 'textarea',
                inputProps: {
                  style: { resize: 'both', overflow: 'auto' },
                },
              }}
            />
            <TextField
              label="ID Canal de Slack"
              value={alert.slackChannelId}
              onChange={(e) => handleAlertChange(alertIndex, 'slackChannelId', e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={alert.isLastCRMUpdatedThreeDaysAgo || false}
                  onChange={(e) => handleAlertChange(alertIndex, 'isLastCRMUpdatedThreeDaysAgo', e.target.checked)}
                />
              }
              label="Verificar si el CRM no se ha actualizado en 3 días"
            />

            {/* Email Notifications Section */}
            <Box mt={2}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" fontWeight="bold">
                  Notificaciones por Email
                </Typography>
                <Typography variant="subtitle2">
                  Se puede enviar notificaciones a todos los emails con la opción emails generales o se puede enviar
                  notificaciones a emails específicos dependiendo de la sucursal del cliente.
                </Typography>
                <RadioGroup
                  value={getEmailNotificationType(alert)}
                  onChange={(e) =>
                    handleEmailTypeChange(alertIndex, e.target.value as 'general' | 'branchOffice' | 'none')
                  }
                  row
                >
                  <FormControlLabel value="none" control={<Radio />} label="Sin notificaciones" />
                  <FormControlLabel value="general" control={<Radio />} label="Emails generales" />
                  <FormControlLabel value="branchOffice" control={<Radio />} label="Emails por sucursal" />
                </RadioGroup>
              </FormControl>

              {/* General Emails Section */}
              {getEmailNotificationType(alert) === 'general' && (
                <Box mt={2}>
                  <Typography variant="subtitle2">Emails para notificaciones generales</Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      label="Email"
                      value={newEmails[alertIndex] || ''}
                      onChange={(e) => setNewEmails((prev) => ({ ...prev, [alertIndex]: e.target.value }))}
                      size="small"
                      type="email"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddGeneralEmail(alertIndex, newEmails[alertIndex] || '');
                        }
                      }}
                    />
                    <Button
                      onClick={() => handleAddGeneralEmail(alertIndex, newEmails[alertIndex] || '')}
                      variant="outlined"
                      size="small"
                    >
                      Agregar Email
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Typography variant="subtitle2">Emails a contactar:</Typography>
                    {alert.emailsToNotify?.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveGeneralEmail(alertIndex, email)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Branch Office Emails Section */}
              {getEmailNotificationType(alert) === 'branchOffice' && (
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Emails por sucursal:
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Se enviarán a los emails de acuerdo a la sucursal del cliente. Si el cliente no tiene sucursal, se
                    enviarán a todos los emails configurados.
                  </Typography>

                  {/* Add new branch office */}
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      label="Nueva sucursal"
                      value={newBranchOffices[alertIndex] || ''}
                      onChange={(e) => setNewBranchOffices((prev) => ({ ...prev, [alertIndex]: e.target.value }))}
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddBranchOffice(alertIndex, newBranchOffices[alertIndex] || '');
                        }
                      }}
                    />
                    <Button
                      onClick={() => handleAddBranchOffice(alertIndex, newBranchOffices[alertIndex] || '')}
                      variant="outlined"
                      size="small"
                    >
                      Agregar Sucursal
                    </Button>
                  </Box>

                  {/* Show existing branch offices and their emails */}
                  {alert.emailsToNotifyByBranchOffice &&
                    Object.entries(alert.emailsToNotifyByBranchOffice).map(([branchOffice, emails]) => (
                      <Box
                        key={branchOffice}
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          p: 2,
                          mb: 1,
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle1">
                            Sucursal: <b>{branchOffice}</b>
                          </Typography>
                          <Button
                            onClick={() => handleRemoveBranchOffice(alertIndex, branchOffice)}
                            color="error"
                            size="small"
                          >
                            Eliminar Sucursal
                          </Button>
                        </Box>

                        {/* Add email to this branch office */}
                        <Box display="flex" gap={1} mb={1}>
                          <TextField
                            label="Email"
                            value={newBranchOfficeEmails[alertIndex]?.[branchOffice] || ''}
                            onChange={(e) =>
                              setNewBranchOfficeEmails((prev) => ({
                                ...prev,
                                [alertIndex]: { ...prev[alertIndex], [branchOffice]: e.target.value },
                              }))
                            }
                            size="small"
                            type="email"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddBranchOfficeEmail(
                                  alertIndex,
                                  branchOffice,
                                  newBranchOfficeEmails[alertIndex]?.[branchOffice] || ''
                                );
                              }
                            }}
                          />
                          <Button
                            onClick={() =>
                              handleAddBranchOfficeEmail(
                                alertIndex,
                                branchOffice,
                                newBranchOfficeEmails[alertIndex]?.[branchOffice] || ''
                              )
                            }
                            variant="outlined"
                            size="small"
                          >
                            Agregar Email
                          </Button>
                        </Box>

                        {/* Show emails for this branch office */}
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          <Typography variant="subtitle2">Emails a contactar:</Typography>
                          {emails.map((email) => (
                            <Chip
                              key={email}
                              label={email}
                              onDelete={() => handleRemoveBranchOfficeEmail(alertIndex, branchOffice, email)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          </Box>
          <Box mt={2} display="flex" flexDirection="column">
            Ejemplos:
            {alert.examples.map((example, exampleIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`example-${exampleIndex}`} display="flex" alignItems="center" mt={1}>
                <TextField
                  value={example}
                  onChange={(e) => handleExampleChange(alertIndex, exampleIndex, e.target.value)}
                  fullWidth
                  sx={{ '& input': { padding: '10px 15px' } }}
                />
                <IconButton onClick={() => handleDeleteExample(alertIndex, exampleIndex)} color="error">
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              onClick={() => handleAddExample(alertIndex)}
              startIcon={<Add />}
              variant="outlined"
              color="primary"
              style={{ marginTop: 10, maxWidth: 200 }}
            >
              Agregar Ejemplo
            </Button>
          </Box>
        </Box>
      ))}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 1 }}>
        <PrimaryBtn
          startIcon={<Add />}
          onClick={handleAddAlert}
          variant="contained"
          color="primary"
          sx={{ maxWidth: 200 }}
        >
          Agregar Alerta
        </PrimaryBtn>

        <PrimaryBtn
          startIcon={<Save />}
          onClick={handleSaveAlertsConfig}
          variant="contained"
          color="primary"
          sx={{ marginLeft: 2, maxWidth: 200 }}
        >
          Guardar Alertas
        </PrimaryBtn>
      </div>
    </Box>
  );
};

export default BusinessUnitAlertsEditor;
